import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/common/Logo";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { invalidateCache } from "../services/cacheService";
import { loadAllLocations } from "../services/locationService";
import { ROUTES } from "../utils/constants";

// Tab config — key matches the property name in loadAllLocations() return value
const TABS = ["Regions", "Zones", "Woredas", "Cities"];
const COLS = {
  Regions: {
    col: "et_regions",
    key: "regions",
    parentLabel: null,
    parentCol: null,
    parentKey: null,
    parentListKey: null,
  },
  Zones: {
    col: "et_zones",
    key: "zones",
    parentLabel: "Region",
    parentCol: "et_regions",
    parentKey: "regionId",
    parentListKey: "regions",
  },
  Woredas: {
    col: "et_woredas",
    key: "woredas",
    parentLabel: "Zone",
    parentCol: "et_zones",
    parentKey: "zoneId",
    parentListKey: "zones",
  },
  Cities: {
    col: "et_cities",
    key: "cities",
    parentLabel: "Woreda",
    parentCol: "et_woredas",
    parentKey: "woredaId",
    parentListKey: "woredas",
  },
};

const LocationManagement = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [tab, setTab] = useState("Regions");
  const [locations, setLocations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState("");
  const [formParentId, setFormParentId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [filterParent, setFilterParent] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const data = await loadAllLocations();
      setLocations(data);
    } catch (e) {
      console.error("[LocationManagement] reload error:", e);
      setMsg({ type: "error", text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const cfg = COLS[tab];

  const parentList = useMemo(() => {
    if (!locations || !cfg.parentListKey) return [];
    return locations[cfg.parentListKey] || [];
  }, [locations, cfg]);

  const items = useMemo(() => {
    if (!locations) return [];
    let list = locations[cfg.key] || [];
    if (filterParent && cfg.parentKey)
      list = list.filter((i) => i[cfg.parentKey] === filterParent);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((i) => i.name.toLowerCase().includes(q));
    }
    return list;
  }, [locations, cfg, search, filterParent]);

  const parentName = (item) => {
    if (!cfg.parentKey || !locations) return "";
    const parentId = item[cfg.parentKey];
    const parentMapKey = cfg.parentListKey + "Map"; // e.g. "regions" → "regionsMap"... wait
    // Use the correct map keys from loadAllLocations
    const mapKey = {
      regions: "regionMap",
      zones: "zoneMap",
      woredas: "woredaMap",
      cities: "cityMap",
    };
    const map = locations[mapKey[cfg.parentListKey]];
    return map?.[parentId]?.name || parentId;
  };

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const openAdd = () => {
    setEditItem(null);
    setFormName("");
    setFormParentId(filterParent || "");
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormName(item.name);
    setFormParentId(item[cfg.parentKey] || "");
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditItem(null);
    setFormName("");
    setFormParentId("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formName.trim()) return;
    if (cfg.parentKey && !formParentId) {
      flash("error", `Please select a ${cfg.parentLabel}`);
      return;
    }
    setSaving(true);
    try {
      const data = { name: formName.trim(), updatedAt: serverTimestamp() };
      if (cfg.parentKey) data[cfg.parentKey] = formParentId;

      if (editItem) {
        await updateDoc(doc(db, cfg.col, editItem.id), data);
        flash("success", `${tab.slice(0, -1)} updated`);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, cfg.col), data);
        flash("success", `${tab.slice(0, -1)} added`);
      }
      await invalidateCache(cfg.col);
      cancelForm();
      await reload();
    } catch (err) {
      flash("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`))
      return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, cfg.col, item.id));
      await invalidateCache(cfg.col);
      flash("success", `${tab.slice(0, -1)} deleted`);
      await reload();
    } catch (err) {
      flash("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } finally {
      navigate(ROUTES.ADMIN_LOGIN, { replace: true });
    }
  };

  const switchTab = (t) => {
    setTab(t);
    setSearch("");
    setFilterParent("");
    setShowForm(false);
    setEditItem(null);
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <Logo size="medium" className="header-logo" />
              <h1 className="dashboard-title">Location Management</h1>
            </div>
            <div className="dashboard-actions">
              <div className="user-info">Welcome, {user?.email}</div>
              <button
                onClick={handleLogout}
                className="logout-btn"
                type="button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">
          {/* Nav */}
          <div className="dashboard-navigation">
            <button
              onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
              className="nav-button"
              type="button"
            >
              🏠 Dashboard
            </button>
            <button
              onClick={() => navigate(ROUTES.REFERRAL_REPORTS)}
              className="nav-button"
              type="button"
            >
              📊 Referral Reports
            </button>
            <button
              onClick={() => navigate(ROUTES.ITEM_MANAGEMENT)}
              className="nav-button"
              type="button"
            >
              🛒 Item Management
            </button>
          </div>

          {/* Flash */}
          {msg.text && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "0.75rem 1rem",
                borderRadius: "0.5rem",
                background:
                  msg.type === "error"
                    ? "var(--color-danger-bg)"
                    : "var(--color-success-bg)",
                color:
                  msg.type === "error"
                    ? "var(--color-danger)"
                    : "var(--color-success)",
                border: `1px solid ${msg.type === "error" ? "var(--color-danger)" : "var(--color-success)"}`,
              }}
            >
              {msg.text}
            </div>
          )}

          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              marginBottom: "1.5rem",
              borderBottom: "2px solid var(--border-primary)",
            }}
          >
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => switchTab(t)}
                style={{
                  padding: "0.625rem 1.25rem",
                  border: "none",
                  borderBottom:
                    tab === t
                      ? "2px solid var(--color-primary)"
                      : "2px solid transparent",
                  background: "none",
                  color:
                    tab === t
                      ? "var(--color-primary)"
                      : "var(--text-secondary)",
                  fontWeight: tab === t ? "600" : "400",
                  cursor: "pointer",
                  marginBottom: "-2px",
                  fontSize: "0.9375rem",
                }}
              >
                {t}
                {locations && (
                  <span
                    style={{
                      marginLeft: "0.375rem",
                      fontSize: "0.75rem",
                      opacity: 0.7,
                    }}
                  >
                    ({(locations[COLS[t].key] || []).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              className="form-input"
              placeholder={`Search ${tab.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: "1", minWidth: "180px", maxWidth: "300px" }}
            />
            {cfg.parentLabel && (
              <select
                className="form-select"
                value={filterParent}
                onChange={(e) => setFilterParent(e.target.value)}
                style={{ flex: "1", minWidth: "160px", maxWidth: "260px" }}
              >
                <option value="">All {cfg.parentLabel}s</option>
                {parentList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={openAdd}
              disabled={saving}
            >
              + Add {tab.slice(0, -1)}
            </button>
            <button
              type="button"
              className="refresh-btn"
              onClick={reload}
              disabled={loading}
            >
              {loading ? "Loading..." : "🔄 Refresh"}
            </button>
          </div>

          {/* Add/Edit Form */}
          {showForm && (
            <div
              className="form-section"
              style={{
                marginBottom: "1.5rem",
                padding: "1.25rem",
                background: "var(--bg-secondary)",
                borderRadius: "0.5rem",
                border: "1px solid var(--border-primary)",
              }}
            >
              <h3
                style={{ marginBottom: "1rem", color: "var(--text-primary)" }}
              >
                {editItem
                  ? `Edit ${tab.slice(0, -1)}`
                  : `Add New ${tab.slice(0, -1)}`}
              </h3>
              <form onSubmit={handleSave}>
                {cfg.parentLabel && (
                  <div className="form-field">
                    <label className="form-label">{cfg.parentLabel} *</label>
                    <select
                      className="form-select"
                      value={formParentId}
                      onChange={(e) => setFormParentId(e.target.value)}
                      required
                      disabled={saving}
                    >
                      <option value="">Select {cfg.parentLabel}</option>
                      {parentList.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-field">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder={`Enter ${tab.slice(0, -1).toLowerCase()} name`}
                    required
                    disabled={saving}
                    autoFocus
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <LoadingSpinner size="small" />
                    ) : editItem ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <LoadingSpinner size="medium" message="Loading locations..." />
          ) : (
            <div className="table-container">
              <table className="registration-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {cfg.parentLabel && <th>{cfg.parentLabel}</th>}
                    <th style={{ width: "120px", textAlign: "center" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={cfg.parentLabel ? 3 : 2}
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        No {tab.toLowerCase()} found
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        {cfg.parentLabel && (
                          <td style={{ color: "var(--text-secondary)" }}>
                            {parentName(item)}
                          </td>
                        )}
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => openEdit(item)}
                            disabled={saving}
                            title="Edit"
                            style={{
                              marginRight: "0.375rem",
                              padding: "0.25rem 0.5rem",
                              fontSize: "1rem",
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(item)}
                            disabled={saving}
                            title="Delete"
                            style={{
                              padding: "0.25rem 0.5rem",
                              fontSize: "1rem",
                            }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                }}
              >
                Showing {items.length} {tab.toLowerCase()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;
